/**
 * MXlab — Barre d'achat sticky mobile, compacte et dépliable.
 *
 * Aucun recalcul de prix, AUCUN second récap maintenu en parallèle :
 *
 * 1. TOTAL — mirroir de `mx-summary [data-mx-grand]` (MutationObserver). Même source
 *    de vérité que le panneau.
 * 2. RANGÉE REPLIÉE — plus de mirroir de contenu depuis le 11/09/2026 (les noms de
 *    matières et le compteur d'accessoires y faisaient doublon avec le tiroir déplié,
 *    qui vient d'être choisi juste au-dessus) : elle ne porte plus que le libellé
 *    « Détail » / chevron, statiques.
 * 3. VISIBILITÉ — IntersectionObserver sur le bouton d'achat du panneau : la barre
 *    n'apparaît QUE quand ce bouton est hors viewport, et se cache dès qu'il revient.
 *    Les deux boutons ne sont donc jamais visibles ensemble (aucun moment sans bouton).
 * 4. TIROIR — à l'ouverture on construit le récap détaillé : CLONE lecture seule de
 *    `mx-summary` (pas de champ de formulaire dedans), et on DÉPLACE l'élément
 *    `mx-note` (un champ ne se duplique pas → valeur préservée), remis au panneau à
 *    la fermeture. Le clone est jeté à la fermeture. Un MutationObserver sur l'ORIGINAL
 *    resynchronise le clone tant que le tiroir reste ouvert (bug corrigé le 11/09/2026 :
 *    changer de matière ou d'accessoire tiroir ouvert ne se voyait qu'à la réouverture,
 *    seul le total — mirroré séparément, point 1 — se mettait à jour tout de suite).
 * 5. VALIDATION — on écoute `mx:validation` (émis par mx-validate, sens UNIQUE) : on
 *    affiche un marqueur rouge compact « N champs à compléter » et on déplie si le
 *    signal vient d'un submit. La barre ne connaît AUCUNE règle de validation.
 */
class MxBuybar extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.moreLabel = this.dataset.moreLabel || 'Afficher le détail';
    this.lessLabel = this.dataset.lessLabel || 'Masquer le détail';

    this.totalEl = this.querySelector('[data-mx-buybar-total]');
    this.toggleBtn = this.querySelector('[data-toggle]');
    this.drawer = this.querySelector('[data-drawer]');
    this.moreLabelEl = this.querySelector('[data-more-label]');
    this.errEl = this.querySelector('[data-err]');

    this.summary = document.querySelector('mx-summary');
    this.note = document.querySelector('mx-note');

    // Gabarits du marqueur d'erreur : on réutilise les réglages déjà posés sur le
    // conteneur de validation (aucun réglage neuf à maintenir).
    const vp = document.querySelector('[data-mx-validate-panel]');
    this.errOne = (vp && vp.dataset.summaryOne) || '{n} champ à compléter';
    this.errMany = (vp && vp.dataset.summaryMany) || '{n} champs à compléter';

    const form = document.getElementById(this.formId);
    this.mainButton =
      form?.querySelector('[ref="addToCartButton"]') ||
      form?.querySelector('[type="submit"][name="add"]') ||
      form?.querySelector('[type="submit"]');

    this.open = false;
    this.errorCount = 0;

    this.mirrorTotal();
    this.watchMainButton();

    this.toggleBtn.addEventListener('click', this.onToggle);
    document.addEventListener('mx:validation', this.onValidation);
  }

  disconnectedCallback() {
    this.intersection?.disconnect();
    this.totalObserver?.disconnect();
    this.drawerSummaryObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.removeEventListener('mx:validation', this.onValidation);
    if (this.raf) cancelAnimationFrame(this.raf);
    // Si on se déconnecte tiroir ouvert, la note doit revenir chez elle.
    this.restoreNote();
  }

  /** Recopie en continu le total du récap. */
  mirrorTotal() {
    const grand = document.querySelector('mx-summary [data-mx-grand]');
    if (!grand || !this.totalEl) return;
    const sync = () => {
      this.totalEl.textContent = grand.textContent;
    };
    sync();
    this.totalObserver = new MutationObserver(sync);
    this.totalObserver.observe(grand, { childList: true, characterData: true, subtree: true });
  }

  onToggle = () => {
    if (this.open) this.collapse();
    else this.expand();
  };

  /** Ouvre le tiroir : construit le récap détaillé (clone récap + note déplacée). */
  expand() {
    if (this.open) return;
    this.open = true;

    const inner = document.createElement('div');
    inner.className = 'mx-buybar__drawer-inner';

    // 1. Récap détaillé — CLONE lecture seule (mx-summary ne contient aucun champ de
    //    formulaire, donc pas de risque de double soumission). Resynchronisé en continu
    //    (voir en-tête) : la copie initiale seule figeait le tiroir au moment de l'ouverture.
    if (this.summary) {
      const recap = document.createElement('div');
      recap.className = 'mx-sum';
      recap.innerHTML = this.summary.innerHTML;
      inner.appendChild(recap);

      this.drawerSummaryObserver = new MutationObserver(() => {
        recap.innerHTML = this.summary.innerHTML;
        this.setDrawerHeight();
      });
      this.drawerSummaryObserver.observe(this.summary, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }

    // 2. Champ de note — DÉPLACÉ (un champ ne se duplique pas ; valeur préservée). On
    //    laisse un PLACEHOLDER de même hauteur à sa place : le panneau garde sa hauteur,
    //    donc AUCUN reflow de la page derrière (pas de saut à l'ouverture/fermeture).
    if (this.note) {
      this.notePlaceholder = document.createElement('div');
      this.notePlaceholder.style.height = this.note.offsetHeight + 'px';
      this.note.parentNode.insertBefore(this.notePlaceholder, this.note);
      inner.appendChild(this.note);
    }

    this.drawer.replaceChildren(inner);
    this.classList.add('is-open');
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    if (this.moreLabelEl) this.moreLabelEl.textContent = this.lessLabel;
    this.setDrawerHeight();
  }

  /** Hauteur du tiroir bornée : le bouton (rangée 2) doit rester à l'écran. */
  setDrawerHeight() {
    const inner = this.drawer.firstElementChild;
    if (!inner) return;
    const action = this.querySelector('.mx-buybar__action');
    const budget =
      window.innerHeight - (action?.offsetHeight || 0) - (this.toggleBtn?.offsetHeight || 0) - 16;
    this.drawer.style.maxHeight = Math.min(inner.scrollHeight, Math.max(budget, 120)) + 'px';
  }

  /** Ferme le tiroir : rend la note au panneau AVANT de vider le clone. */
  collapse() {
    if (!this.open) return;
    this.open = false;
    this.drawerSummaryObserver?.disconnect();
    this.drawerSummaryObserver = null;
    this.restoreNote();
    this.drawer.style.maxHeight = '0px';
    this.classList.remove('is-open');
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    if (this.moreLabelEl) this.moreLabelEl.textContent = this.moreLabel;
    clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => {
      if (!this.open) this.drawer.replaceChildren();
    }, 240);
  }

  /** Remet l'élément note à sa place (celle du placeholder) et retire le placeholder. */
  restoreNote() {
    if (this.note && this.notePlaceholder && this.notePlaceholder.parentNode) {
      this.notePlaceholder.parentNode.insertBefore(this.note, this.notePlaceholder);
      this.notePlaceholder.remove();
    }
    this.notePlaceholder = null;
  }

  /** Signal de validation (sens unique). La barre affiche/replie, sans connaître les règles. */
  onValidation = (event) => {
    const { count = 0, fromSubmit } = event.detail || {};
    this.errorCount = count;
    const has = count > 0;
    this.classList.toggle('has-error', has);
    if (this.errEl) {
      this.errEl.hidden = !has;
      if (has) {
        const tpl = count === 1 ? this.errOne : this.errMany;
        this.errEl.textContent = tpl.replace('{n}', count);
      }
    }
    // Déplie au submit invalide — mais si le client REPLIE ensuite, le marqueur rouge
    // ci-dessus persiste (piloté par errorCount, pas par l'état ouvert/fermé).
    if (fromSubmit && has && this.classList.contains('is-visible')) this.expand();
  };

  /** Affiche la barre quand le bouton d'achat du panneau n'est pas visible. */
  watchMainButton() {
    if (!this.mainButton) {
      this.classList.add('is-visible');
      return;
    }

    this.update = () => {
      const rect = this.mainButton.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const inView = rect.bottom > 0 && rect.top < vh;
      const visible = !inView;
      this.classList.toggle('is-visible', visible);
      // Barre cachée (le panneau et son bouton sont à l'écran) : on referme le tiroir
      // → la note repart dans le panneau, jamais coincée dans un tiroir masqué.
      if (!visible && this.open) this.collapse();
    };

    this.update();

    if ('IntersectionObserver' in window) {
      this.intersection = new IntersectionObserver(() => this.update(), { threshold: 0 });
      this.intersection.observe(this.mainButton);
    }

    this.onScroll = () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    window.addEventListener('load', this.update, { once: true });
  }
}

if (!customElements.get('mx-buybar')) {
  customElements.define('mx-buybar', MxBuybar);
}
